import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Trans } from 'react-i18next'

import { AppDialog } from '@/components/AppDialog'
import { AppTooltip } from '@/components/AppTooltip'
import InfoIcon from '@/components/icons/info.svg?react'
import { Button } from '@/components/ui/button'

import storeSelectImage from './store-select-example.png'

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const PRIMARK_CLICK_COLLECT_URL = 'https://www.primark.com/en-gb/click-and-collect'
const PRIMARK_VIDEO_EMBED_URL = 'https://player.vimeo.com/video/1197072519'
const PRIMARK_VIDEO_SECTION_ID = 'primark-how-it-works-video'

export function PrimarkExplainDialog({ isOpen, onOpenChange }: Props) {
  const { t } = useTranslation()
  const videoSectionRef = useRef<HTMLDivElement>(null)
  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={open => {
        onOpenChange(open)
      }}
      className='max-w-lg'
      titleClassName='px-8'
      footerActionsClassName='justify-center sm:justify-center p-4 items-center'
      footerActions={
        <Button
          onClick={() => onOpenChange(false)}
          className='h-12 w-[335px] rounded-full bg-[#fad600] text-sm font-medium text-[#333] hover:bg-[#fad600]/90'
        >
          {t('primark.howItWorksCloseButton')}
        </Button>
      }
      title={
        <div className='flex flex-col items-center gap-2'>
          <div className='flex size-6 items-center justify-center'>
            <InfoIcon className='size- text-[#19b5f0]' aria-hidden />
          </div>
          <p className='text-lg leading-6 font-medium text-[#333]'>{t('primark.howItWorksTitle')}</p>
          <a
            href={`#${PRIMARK_VIDEO_SECTION_ID}`}
            className='text-text-link text-sm leading-4 underline'
            onClick={event => {
              event.preventDefault()
              videoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          >
            {t('primark.howItWorksVideoLink')}
          </a>
        </div>
      }
    >
      <div className='flex flex-col items-center gap-4 px-6 pt-2 pb-6 text-center'>
        <div className='flex w-full flex-col gap-4 text-center'>
          {[
            {
              step: 1,
              content: (
                <Trans
                  i18nKey='primark.howItWorksStep1'
                  components={{
                    Link: (
                      <a
                        href={PRIMARK_CLICK_COLLECT_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-text-link cursor-pointer underline'
                      />
                    ),
                  }}
                />
              ),
            },
            {
              step: 2,
              content: (
                <>
                  <Trans i18nKey='primark.howItWorksStep2Text' />
                  <br />
                  <AppTooltip
                    disableHover
                    contentClassName='bg-card max-w-sm'
                    triggerProps={{ className: 'text-text-link underline' }}
                    content={
                      <div className='bg-muted/30 border-border-default mt-2 flex aspect-video w-full items-center justify-center rounded-lg border'>
                        <img src={storeSelectImage} alt='' className='max-h-full max-w-full object-contain' />
                      </div>
                    }
                  >
                    {t('primark.howItWorksStep2ShowExample')}
                  </AppTooltip>
                </>
              ),
            },
            { step: 3, content: <Trans i18nKey='primark.howItWorksStep3' /> },
            { step: 4, content: <Trans i18nKey='primark.howItWorksStep4' /> },
            { step: 5, content: <Trans i18nKey='primark.howItWorksStep5' /> },
          ].map(({ step, content }) => (
            <div key={step} className='flex flex-col gap-2'>
              <p className='text-sm leading-4 font-medium text-[#333]'>{step}.</p>
              <div className='text-sm text-[#616161]'>{content}</div>
            </div>
          ))}
        </div>
        <div
          ref={videoSectionRef}
          id={PRIMARK_VIDEO_SECTION_ID}
          className='bg-muted/30 border-border-default relative aspect-[40/71] w-full scroll-mt-4 overflow-hidden rounded-lg border'
        >
          <iframe
            src={PRIMARK_VIDEO_EMBED_URL}
            className='absolute inset-0 size-full'
            allow='autoplay; fullscreen; picture-in-picture'
            title={t('primark.howItWorksTitle')}
          />
        </div>
      </div>
    </AppDialog>
  )
}
